import { useQuery, useQueryClient } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import React, { useState } from 'react'
import useDetailTabs from 'src/component-layout/DetailTabs/useDetailTabs'
import MfaStepUpModal from 'src/component-modal/MfaStepUpModal/MfaStepUpModal'
import {
  formatPaymentAmount,
  formatPaymentHistoryDate,
  paymentStatusPresentation,
} from 'src/feature/PaymentHistory/paymentHistoryPresentation'
import { useTranslate } from 'src/localization/Trns'
import { openBillPaymentInvoice } from 'src/queries/payments/payments.download'
import type {
  BillPaymentReconciliationResult,
  BillPaymentSecondaryActionResult,
} from 'src/queries/payments/payments.flow.types'
import {
  paymentsKeys,
  paymentsQueries,
  useCancelBillPayment,
  useResendBillPaymentAuthorization,
  useRestartBillPaymentAuthorization,
} from 'src/queries/payments/payments.queries'
import type {
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentHistoryBankLabel,
} from 'src/queries/payments/payments.types'
import { createBillPaymentIdempotencyKey } from 'src/queries/payments/payments.utils'

import style from './PaymentDetailFlyout.style'

interface PaymentDetailFlyoutProps {
  organizationId: string
  viewerUserId: string
  paymentId?: string
  onClose: () => void
  onOpenBankAccounts: () => void
  onOpenVendors: () => void
}

const bankLabel = (value: BillPaymentHistoryBankLabel | null): string =>
  value
    ? `${value.label}${value.lastFour ? ` •••• ${value.lastFour}` : ''}`
    : '—'

const PAYMENT_DETAIL_TABS = ['overview', 'activity'] as const

const PaymentDetailFlyout: React.FC<PaymentDetailFlyoutProps> = ({
  organizationId,
  viewerUserId,
  paymentId,
  onClose,
  onOpenBankAccounts,
  onOpenVendors,
}) => {
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'overview' | 'activity'>('overview')
  const [mfaDetails, setMfaDetails] = useState<BillPaymentDetailsResponse>()
  const [actionError, setActionError] = useState<string>()
  const { getPanelProps, getTabProps } = useDetailTabs({
    activeTab: tab,
    onTabChange: setTab,
    tabs: PAYMENT_DETAIL_TABS,
  })
  const detailQuery = useQuery(
    paymentsQueries.paymentDetail(organizationId, viewerUserId, paymentId),
  )
  const cancelPayment = useCancelBillPayment(organizationId, viewerUserId)
  const resendAuthorization = useResendBillPaymentAuthorization(
    organizationId,
    viewerUserId,
  )
  const restartAuthorization = useRestartBillPaymentAuthorization(
    organizationId,
    viewerUserId,
  )
  const details = detailQuery.data
  const history = details?.history
  const actions = history?.payment.availableActions ?? []

  const invalidateHistory = (): void => {
    void queryClient.invalidateQueries({
      queryKey: paymentsKeys.paymentHistories(),
    })
  }

  const reconcileHistory = async (): Promise<void> => {
    invalidateHistory()
    await detailQuery.refetch()
  }

  const openResume = async (): Promise<void> => {
    if (!details) return
    setActionError(undefined)
    if (details.authorization) {
      setMfaDetails(details)
      return
    }
    try {
      const restarted = await restartAuthorization.mutateAsync({
        paymentId: details.payment.id,
        revision: details.payment.revision,
        idempotencyKey: createBillPaymentIdempotencyKey(),
      })
      setMfaDetails(restarted)
    } catch {
      setActionError(
        translate('We could not resume this payment. Please try again.'),
      )
      await reconcileHistory()
    }
  }

  const updateMfaDetails = (
    next: BillPaymentDetailsResponse,
  ): BillPaymentSecondaryActionResult => {
    setMfaDetails(next)
    return { kind: 'updated', details: next }
  }

  const reconcile = async (
    error: BillPaymentError,
  ): Promise<BillPaymentReconciliationResult> => {
    const result = await detailQuery.refetch()
    if (!result.data) return { kind: 'unavailable', error }
    setMfaDetails(result.data)
    if (result.data.payment.state === 'SCHEDULED') {
      return { kind: 'scheduled', details: result.data }
    }
    if (result.data.authorization) {
      return {
        kind: 'authorization',
        details: result.data,
        sameAttempt: false,
      }
    }
    return { kind: 'terminal', details: result.data }
  }

  const cancel = async (): Promise<void> => {
    if (!details) return
    setActionError(undefined)
    try {
      await cancelPayment.mutateAsync({
        paymentId: details.payment.id,
        revision: details.payment.revision,
        idempotencyKey: createBillPaymentIdempotencyKey(),
      })
    } catch (error) {
      const code =
        error && typeof error === 'object' && 'errorCode' in error
          ? String(error.errorCode)
          : undefined
      if (code === 'EXECUTION_IN_FLIGHT') {
        setActionError(
          translate(
            'This payment is already processing and can no longer be cancelled.',
          ),
        )
      } else if (code === 'PAYMENT_PRECONDITION_FAILED') {
        setActionError(
          translate('This payment changed. We refreshed the latest details.'),
        )
      } else {
        setActionError(
          translate('We could not cancel this payment. Please try again.'),
        )
      }
      await reconcileHistory()
    }
  }

  const downloadInvoice = async (): Promise<void> => {
    const attachmentId = history?.payment.invoiceAttachment?.id
    if (!attachmentId) return
    setActionError(undefined)
    try {
      await openBillPaymentInvoice(queryClient, organizationId, attachmentId)
    } catch {
      setActionError(
        translate('We could not download this invoice. Please try again.'),
      )
    }
  }

  return (
    <>
      <BaseFlyout
        className="PaymentDetailFlyout"
        css={style}
        isOpen={Boolean(paymentId)}
        onClose={() => {
          setTab('overview')
          setMfaDetails(undefined)
          setActionError(undefined)
          onClose()
        }}
        aria-label={translate('Payment details')}
      >
        {detailQuery.isLoading ? (
          <div className="payment-detail-message">
            {translate('Loading payment…')}
          </div>
        ) : detailQuery.isError || !details ? (
          <div className="payment-detail-message">
            <p>{translate('We could not load this payment.')}</p>
            <Button
              variant="secondary"
              onClick={() => void detailQuery.refetch()}
            >
              {translate('Retry')}
            </Button>
          </div>
        ) : !history ? (
          <div className="payment-detail-message">
            <h2>{translate('Payment details')}</h2>
            <p>
              {translate(
                'Detailed payment history is temporarily unavailable. Please refresh in a moment.',
              )}
            </p>
          </div>
        ) : (
          <div className="payment-detail-layout">
            <header className="payment-detail-header">
              <h2>{history.payment.vendor.name}</h2>
              <div className="payment-detail-subtitle">
                <span>
                  {translate(
                    history.payment.displayStatus === 'COMPLETED'
                      ? 'Paid ${date}'
                      : 'Due ${date}',
                    {
                      date: formatPaymentHistoryDate(
                        history.payment.displayStatus === 'COMPLETED'
                          ? history.paymentDate
                          : history.payment.invoiceDueDate,
                      ),
                    },
                  )}
                </span>
                <span aria-hidden="true">{'·'}</span>
                <span>
                  {formatPaymentAmount(
                    history.payment.amountCents,
                    history.payment.currency,
                  )}
                </span>
                <span aria-hidden="true">{'·'}</span>
                <span>{`#${history.payment.invoiceNumber}`}</span>
                <span aria-hidden="true">{'·'}</span>
                <Pill
                  size="medium"
                  color={
                    paymentStatusPresentation(history.payment.displayStatus)
                      .color
                  }
                >
                  {translate(
                    paymentStatusPresentation(history.payment.displayStatus)
                      .label,
                  )}
                </Pill>
              </div>
            </header>
            {history.payment.displayStatus === 'FAILED' && (
              <div className="payment-detail-alert">
                <AlertPanel variant="severe" role="alert">
                  <strong>{translate('Payment failed')}</strong>
                  <span>
                    {translate('This payment could not be completed.')}
                  </span>
                </AlertPanel>
              </div>
            )}
            {actionError && (
              <div className="payment-detail-alert">
                <AlertPanel variant="severe" role="alert">
                  {actionError}
                </AlertPanel>
              </div>
            )}
            <div
              className="payment-detail-tabs"
              role="tablist"
              aria-label={translate('Payment details')}
            >
              <button
                {...getTabProps('overview')}
              >
                {translate('Overview')}
              </button>
              <button
                {...getTabProps('activity')}
              >
                {translate('Payment Activity')}
              </button>
            </div>
            <div className="payment-detail-scroll">
              {tab === 'overview' ? (
                <div
                  {...getPanelProps('overview')}
                >
                  <section>
                    <h3>{translate('Invoice Details')}</h3>
                    <dl>
                      <div>
                        <dt>{translate('Amount')}</dt>
                        <dd>
                          {formatPaymentAmount(
                            history.payment.amountCents,
                            history.payment.currency,
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Due Date')}</dt>
                        <dd>
                          {formatPaymentHistoryDate(
                            history.payment.invoiceDueDate,
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Invoice Number')}</dt>
                        <dd>{history.payment.invoiceNumber}</dd>
                      </div>
                      <div>
                        <dt>{translate('Invoice Date')}</dt>
                        <dd>{formatPaymentHistoryDate(history.invoiceDate)}</dd>
                      </div>
                      {history.payment.invoiceAttachment && (
                        <div>
                          <dt>{translate('Invoice')}</dt>
                          <dd>
                            <button
                              type="button"
                              className="payment-detail-link"
                              onClick={() => void downloadInvoice()}
                            >
                              {history.payment.invoiceNumber}
                            </button>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </section>
                  <section>
                    <h3>{translate('Payment Details')}</h3>
                    <dl>
                      <div>
                        <dt>{translate('Payment Method')}</dt>
                        <dd>{bankLabel(history.paymentMethod)}</dd>
                      </div>
                      <div>
                        <dt>{translate('Vendor Bank Account')}</dt>
                        <dd>
                          {history.vendorBankAccount ? (
                            <button
                              type="button"
                              className="payment-detail-link"
                              onClick={onOpenVendors}
                            >
                              {bankLabel(history.vendorBankAccount)}
                            </button>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Bill Payer Bank Account')}</dt>
                        <dd>
                          {history.payerBankAccount ? (
                            <button
                              type="button"
                              className="payment-detail-link"
                              onClick={onOpenBankAccounts}
                            >
                              {bankLabel(history.payerBankAccount)}
                            </button>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Payment Date')}</dt>
                        <dd>{formatPaymentHistoryDate(history.paymentDate)}</dd>
                      </div>
                      <div>
                        <dt>{translate('Remittance Email')}</dt>
                        <dd>{history.remittanceEmail ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>{translate('Paid By')}</dt>
                        <dd>
                          {history.payment.sender.displayName ??
                            history.payment.sender.email ??
                            '—'}
                        </dd>
                      </div>
                    </dl>
                  </section>
                  {history.memo && (
                    <section>
                      <h3>{translate('Memo')}</h3>
                      <p>{history.memo}</p>
                    </section>
                  )}
                </div>
              ) : (
                <section
                  {...getPanelProps('activity')}
                >
                  <h3>{translate('Status')}</h3>
                  <ol className="payment-activity-list">
                    {history.activities.map((activity) => (
                      <li key={activity.id}>
                        <span
                          className="payment-activity-marker"
                          aria-hidden="true"
                        />
                        <strong>
                          {translate(
                            paymentStatusPresentation(activity.displayStatus)
                              .label,
                          )}
                        </strong>
                        <span>
                          {activity.actor?.displayName ??
                            activity.actor?.email ??
                            translate('Extend')}
                        </span>
                        <time>
                          {new Date(activity.createdAt).toLocaleString()}
                        </time>
                      </li>
                    ))}
                  </ol>
                </section>
              )}
            </div>
            {actions.length > 0 && (
              <footer className="payment-detail-footer">
                {actions.includes('CANCEL') && (
                  <Button
                    variant="secondary"
                    disabled={cancelPayment.isPending}
                    onClick={() => void cancel()}
                  >
                    {translate('Cancel payment')}
                  </Button>
                )}
                {actions.includes('RESUME_AUTHORIZATION') && (
                  <Button variant="primary" onClick={() => void openResume()}>
                    {translate('Resume payment')}
                  </Button>
                )}
              </footer>
            )}
          </div>
        )}
      </BaseFlyout>
      {mfaDetails?.authorization && (
        <MfaStepUpModal
          open
          payment={mfaDetails.payment}
          authorization={mfaDetails.authorization}
          onClose={() => setMfaDetails(undefined)}
          onResend={async () => {
            const authorization = mfaDetails.authorization
            if (!authorization) {
              return { kind: 'terminal', details: mfaDetails }
            }
            return updateMfaDetails(
              await resendAuthorization.mutateAsync({
                paymentId: mfaDetails.payment.id,
                revision: mfaDetails.payment.revision,
                authorizationId: authorization.authorizationId,
                idempotencyKey: createBillPaymentIdempotencyKey(),
              }),
            )
          }}
          onRestart={async () =>
            updateMfaDetails(
              await restartAuthorization.mutateAsync({
                paymentId: mfaDetails.payment.id,
                revision: mfaDetails.payment.revision,
                idempotencyKey: createBillPaymentIdempotencyKey(),
              }),
            )
          }
          onReconcileSchedule={reconcile}
          onScheduled={() => {
            setMfaDetails(undefined)
          }}
          onTerminalError={() => {
            setMfaDetails(undefined)
            void reconcileHistory()
          }}
        />
      )}
    </>
  )
}

export default PaymentDetailFlyout
